import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { scrollToSectionById, setPendingHomeScroll } from '../../utils/smoothScroll';
import { useMenu } from '../../context/MenuContext';
import { isHomePath, PATHS } from '../../navigation/paths';
import { COLORS, SPACING, navLinkTopBarSx } from '../../theme/designTokens';
import { useTranslation } from '../../content/useTranslation';
import MenuToggle from './MenuToggle';
import SiteLogo from './SiteLogo';

const TOPBAR_HEIGHT = 72;

const GradientBlur: React.FC = () => (
  <Box
    aria-hidden
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '120%',
      zIndex: -1,
      pointerEvents: 'none',
      '& > div': {
        position: 'absolute',
        inset: 0,
      },
      '& > div:nth-of-type(1)': {
        backdropFilter: 'blur(0.5px)',
        WebkitBackdropFilter: 'blur(0.5px)',
        maskImage: 'linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 14%, rgba(0,0,0,0) 28%)',
        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 14%, rgba(0,0,0,0) 28%)',
      },
      '& > div:nth-of-type(2)': {
        backdropFilter: 'blur(1.5px)',
        WebkitBackdropFilter: 'blur(1.5px)',
        maskImage: 'linear-gradient(to top, rgba(0,0,0,0) 10%, rgba(0,0,0,0.6) 24%, rgba(0,0,0,0) 38%)',
        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0) 10%, rgba(0,0,0,0.6) 24%, rgba(0,0,0,0) 38%)',
      },
      '& > div:nth-of-type(3)': {
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        maskImage: 'linear-gradient(to top, rgba(0,0,0,0) 20%, rgba(0,0,0,0.7) 36%, rgba(0,0,0,0) 52%)',
        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0) 20%, rgba(0,0,0,0.7) 36%, rgba(0,0,0,0) 52%)',
      },
      '& > div:nth-of-type(4)': {
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        maskImage: 'linear-gradient(to top, rgba(0,0,0,0) 32%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0) 68%)',
        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0) 32%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0) 68%)',
      },
      '& > div:nth-of-type(5)': {
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        maskImage: 'linear-gradient(to top, rgba(0,0,0,0) 44%, rgba(0,0,0,0.85) 62%, rgba(0,0,0,0) 80%)',
        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0) 44%, rgba(0,0,0,0.85) 62%, rgba(0,0,0,0) 80%)',
      },
      '& > div:nth-of-type(6)': {
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        maskImage: 'linear-gradient(to top, rgba(0,0,0,0) 56%, rgba(0,0,0,0.9) 76%, rgba(0,0,0,1) 100%)',
        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0) 56%, rgba(0,0,0,0.9) 76%, rgba(0,0,0,1) 100%)',
      },
      '& > div:nth-of-type(7)': {
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        maskImage: 'linear-gradient(to top, rgba(0,0,0,0) 68%, rgba(0,0,0,0.9) 86%, rgba(0,0,0,1) 100%)',
        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0) 68%, rgba(0,0,0,0.9) 86%, rgba(0,0,0,1) 100%)',
      },
    }}
  >
    <div />
    <div />
    <div />
    <div />
    <div />
    <div />
    <div />
  </Box>
);

interface SiteTopBarProps {
  visible?: boolean;
  variant?: 'hero' | 'sticky';
  showLogo?: boolean;
}

const SiteTopBar: React.FC<SiteTopBarProps> = ({
  visible = true,
  variant = 'sticky',
  showLogo = true,
}) => {
  const { menuOpen, toggleMenu, appearance, setAppearance } = useMenu();
  const { t, lang } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isHero = variant === 'hero';
  const isHome = isHomePath(location.pathname);
  const homePath = lang === 'en' ? PATHS.en : PATHS.home;
  const [hasScrolled, setHasScrolled] = useState(() => window.scrollY > 10);

  useEffect(() => {
    const onScroll = () => setHasScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleToggle = () => {
    if (!menuOpen && isHero) {
      setAppearance({ bg: COLORS.bg, theme: 'light' });
    }
    toggleMenu();
  };

  const isDark = isHero ? true : appearance.theme === 'dark';
  const textColor = isHero ? '#fff' : isDark ? COLORS.textOnDark : COLORS.text;

  return (
    <Box
      sx={{
        position: isHero ? 'absolute' : 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: `${TOPBAR_HEIGHT}px`,
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        pt: '12px',
        px: SPACING.sectionPaddingX,
        zIndex: isHero ? 2 : 100,
        overflow: 'visible',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.4s ease, color 0.35s ease',
      }}
    >
      {!isHero && hasScrolled && <GradientBlur />}
      <Box sx={{ justifySelf: 'start' }}>
        <MenuToggle open={menuOpen} onClick={handleToggle} color={textColor} />
      </Box>

      {showLogo ? (
        <Box
          component={isHome ? 'button' : Link}
          {...(isHome
            ? {
                type: 'button' as const,
                'aria-label': 'Zöllner Lorenz',
                onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
              }
            : { to: homePath })}
          sx={{
            justifySelf: 'center',
            display: 'flex',
            textDecoration: 'none',
            background: 'none',
            border: 'none',
            p: 0,
            cursor: 'pointer',
          }}
        >
          <SiteLogo onDark={isDark} />
        </Box>
      ) : (
        <Box sx={{ justifySelf: 'center' }} />
      )}

      <Typography
        component="button"
        onClick={() => {
          if (isHome) {
            scrollToSectionById('kontakt', { initialDelay: 0 });
          } else {
            setPendingHomeScroll('kontakt');
            navigate(homePath);
          }
        }}
        sx={{
          ...navLinkTopBarSx,
          color: textColor,
          cursor: 'pointer',
          justifySelf: 'end',
          textDecoration: 'none',
          transition: 'color 0.35s ease',
          background: 'none',
          border: 'none',
          p: 0,
          display: menuOpen ? 'none' : 'block',
        }}
      >
        {t.kontaktButton}
      </Typography>
    </Box>
  );
};

export { TOPBAR_HEIGHT };
export default SiteTopBar;
